import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import KeyboardAwareView from '../../../src/components/KeyboardAwareView';
import { showAlert } from '../../../src/utils/alert';
import { contactsApi } from '../../../src/api/client';
import { currentVersion, platformLabel } from '../../../src/utils/appVersion';
import { useVersionStatus } from '../../../src/hooks/useVersionStatus';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import ScreenHeader from '../../../src/components/ScreenHeader';

const CATEGORIES = [
  { key: 'bug', icon: '\u{1F41B}', label: 'Bug', desc: 'Report crashes, errors, or unexpected behavior' },
  { key: 'feature', icon: '\u{1F4A1}', label: 'Feature', desc: 'Suggest improvements or new features' },
  { key: 'billing', icon: '\u{1F4B3}', label: 'Billing', desc: 'Invoices and Enterprise plan questions' },
  { key: 'account', icon: '\u{1F464}', label: 'Account', desc: 'Login problems, profile issues' },
  { key: 'technical', icon: '\u{1F527}', label: 'Technical', desc: 'Sync issues, performance problems' },
  { key: 'general', icon: '\u{1F4AC}', label: 'General', desc: 'Other questions or comments' },
] as const;

const APP_VERSION = currentVersion();
const PLATFORM_LABEL = platformLabel();

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  content: { flex: 1 },
  contentInner: { padding: 16, paddingBottom: 40 },
  label: { fontSize: 12, fontWeight: '700' as const, color: theme.colors.textMuted, letterSpacing: 0.5, marginBottom: 8, marginTop: 20 },
  categoryGrid: { flexDirection: 'row' as const, flexWrap: 'wrap' as const, gap: 8 },
  categoryCard: { width: '31%' as const, backgroundColor: theme.colors.background, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, padding: 12, alignItems: 'center' as const, gap: 4 },
  categoryCardActive: { borderColor: theme.colors.primary, backgroundColor: theme.colors.primaryBg },
  categoryIcon: { fontSize: 22 },
  categoryLabel: { fontSize: 12, fontWeight: '600' as const, color: theme.colors.textSecondary },
  categoryLabelActive: { color: theme.colors.primaryDark },
  categoryDesc: { fontSize: 13, color: theme.colors.textMuted, marginTop: 8, fontStyle: 'italic' as const },
  input: { backgroundColor: theme.colors.background, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 14, fontSize: 16, color: theme.colors.textPrimary },
  charCount: { fontSize: 11, color: theme.colors.textDisabled, textAlign: 'right' as const, marginTop: 4 },
  textArea: { backgroundColor: theme.colors.background, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 14, fontSize: 15, color: theme.colors.textPrimary, minHeight: 150 },
  infoCard: { backgroundColor: theme.colors.background, borderRadius: 12, overflow: 'hidden' as const, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 4, elevation: 2 },
  infoRow: { flexDirection: 'row' as const, justifyContent: 'space-between' as const, alignItems: 'center' as const, padding: 14 },
  infoLabel: { fontSize: 14, fontWeight: '500' as const, color: theme.colors.textMuted },
  infoValue: { fontSize: 14, fontWeight: '600' as const, color: theme.colors.textPrimary },
  infoValueUpdate: { fontSize: 14, fontWeight: '600' as const, color: theme.colors.primary },
  divider: { height: 1, backgroundColor: theme.colors.bgTertiary, marginHorizontal: 14 },
  tipBox: { backgroundColor: theme.colors.primaryBg, borderRadius: 10, padding: 14, marginTop: 16 },
  tipText: { fontSize: 13, color: theme.colors.primary, lineHeight: 18 },
});

export default function ContactScreen() {
  const router = useRouter();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();
  // Callers may name the category they are writing about — Settings sends
  // 'billing' from the Enterprise row. Validated against CATEGORIES rather than
  // trusted, so a stale or hand-edited link lands on the normal empty form
  // instead of a selection the submit button cannot satisfy.
  const params = useLocalSearchParams<{ category?: string }>();
  const presetCategory = CATEGORIES.some((c) => c.key === params.category)
    ? (params.category as string)
    : '';
  const [category, setCategory] = useState<string>(presetCategory);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { status: versionStatus } = useVersionStatus();

  const isValid = category && subject.trim().length >= 3 && message.trim().length >= 10;

  const handleSubmit = async () => {
    if (!isValid) return;

    setIsSubmitting(true);
    try {
      await contactsApi.submit({
        category,
        subject: subject.trim(),
        message: message.trim(),
        app_version: APP_VERSION,
        platform: PLATFORM_LABEL,
      });
      showAlert(
        'Thank you!',
        'Your message has been sent. We\'ll get back to you as soon as possible.',
        () => router.back()
      );
    } catch {
      showAlert('Error', 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <KeyboardAwareView style={styles.container}>
      <ScreenHeader
        title="Contact Us"
        left={{
          label: 'Go back',
          icon: 'arrow-back',
          onPress: () => router.back(),
          testID: 'contact.back-button',
        }}
        right={{
          label: 'Send message',
          text: 'Send',
          tone: 'primary',
          onPress: handleSubmit,
          disabled: isSubmitting || !isValid,
          loading: isSubmitting,
          testID: 'contact.send-button',
        }}
      />
      <ScrollView style={styles.content} contentContainerStyle={styles.contentInner} keyboardShouldPersistTaps="handled">
        {/* Category */}
        <Text style={styles.label}>CATEGORY *</Text>
        <View style={styles.categoryGrid} accessibilityRole="radiogroup" accessibilityLabel="Category">
          {CATEGORIES.map((cat) => (
            <TouchableOpacity
              key={cat.key}
              style={[styles.categoryCard, category === cat.key && styles.categoryCardActive]}
              onPress={() => setCategory(cat.key)}
              accessibilityRole="button"
              accessibilityState={{ checked: category === cat.key }}
              accessibilityLabel={cat.label}
              testID={`contact.category-${cat.key}`}
            >
              <Text style={styles.categoryIcon} accessible={false}>{cat.icon}</Text>
              <Text style={[styles.categoryLabel, category === cat.key && styles.categoryLabelActive]} accessible={false}>
                {cat.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        {category ? (
          <Text style={styles.categoryDesc}>
            {CATEGORIES.find((c) => c.key === category)?.desc}
          </Text>
        ) : null}

        {/* Subject */}
        <Text style={styles.label}>SUBJECT *</Text>
        <TextInput
          style={styles.input}
          value={subject}
          onChangeText={setSubject}
          placeholder="Brief summary of your message..."
          placeholderTextColor={colors.placeholder}
          maxLength={200}
          accessibilityLabel="Subject"
          testID="contact.subject-input"
        />
        <Text style={styles.charCount}>{subject.length}/200</Text>

        {/* Message */}
        <Text style={styles.label}>MESSAGE *</Text>
        <TextInput
          style={styles.textArea}
          value={message}
          onChangeText={setMessage}
          placeholder="Please describe your issue or feedback in detail..."
          placeholderTextColor={colors.placeholder}
          multiline
          textAlignVertical="top"
          maxLength={10000}
          accessibilityLabel="Message"
          testID="contact.message-input"
        />
        <Text style={styles.charCount}>{message.length}/10,000</Text>

        {/* App Info */}
        <Text style={styles.label}>APP INFO</Text>
        <View style={styles.infoCard}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Version</Text>
            <Text style={styles.infoValue}>{APP_VERSION}</Text>
          </View>
          <View style={styles.divider} />
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Platform</Text>
            <Text style={styles.infoValue}>{PLATFORM_LABEL}</Text>
          </View>
          {/* Informational only — no reload affordance here, unlike the About
              screen. Reloading this screen would discard whatever the user has
              already typed, and someone reporting a bug is the last person who
              should lose their message. 'unknown' renders no row at all. */}
          {versionStatus !== 'unknown' && (
            <>
              <View style={styles.divider} />
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Status</Text>
                <Text
                  style={versionStatus === 'outdated' ? styles.infoValueUpdate : styles.infoValue}
                  testID="contact.version-status"
                >
                  {versionStatus === 'outdated' ? 'Update available' : 'Up to date'}
                </Text>
              </View>
            </>
          )}
        </View>

        {/* Tip */}
        <View style={styles.tipBox}>
          <Text style={styles.tipText}>
            For bugs, please include steps to reproduce the issue. For billing inquiries, we typically respond within 24-48 hours.
          </Text>
        </View>
      </ScrollView>
    </KeyboardAwareView>
  );
}
